/** What the Processing place reads beyond the document list - ADR-0212.
 *
 * **Counts are the server's.** `GET /evidence/counts` groups the whole library by state in the
 * database, so a tile on Upload, Processing or Documents prints the number the list is cut from and
 * never a count of the page held (a page of 50 would otherwise read as the library).
 *
 * **The classification and the vendor are advice** (SEC-05, ADR-0165): the preview writes nothing,
 * and a vendor is linked only by `useSetDocumentVendor`, which is a person pressing Confirm. This file
 * has no hook that resolves a string a document says to a row.
 */

import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { evidenceKeys } from "./evidence";
import type { DocumentRow } from "./evidence";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type DocumentCounts = components["schemas"]["DocumentCountsOut"];
export type ClassificationPreview = components["schemas"]["ClassificationPreviewOut"];
export type VendorSuggestion = components["schemas"]["VendorSuggestionOut"];

export const processingKeys = {
  counts: () => ["evidence", "counts"] as const,
  classification: (documentId: string, facilityId: string, fuelUse: string) =>
    ["evidence", "documents", documentId, "classification", facilityId, fuelUse] as const,
  vendor: (documentId: string) => ["evidence", "documents", documentId, "vendor"] as const,
};

/** How many documents are in each state and stage - **the whole library**, unfiltered. */
export function useDocumentCounts(enabled = true) {
  return useQuery({
    queryKey: processingKeys.counts(),
    enabled,
    queryFn: async (): Promise<DocumentCounts> => {
      const { data, error, response } = await api.GET("/api/v1/evidence/counts", {
        params: { query: {} },
      });
      if (error || !data) throw refusalFrom(response, error, "The document counts could not be read.");
      return data;
    },
  });
}

/** The count for one state, or `undefined` until the counts are read. `undefined` is not zero: a
 * tile that printed 0 for a read in flight would be the library claiming to be empty. */
export function stateCount(counts: DocumentCounts | undefined, ...states: string[]): number | undefined {
  if (!counts) return undefined;
  const byState = counts.by_state as unknown as Record<string, number>;
  return states.reduce((sum, state) => sum + (byState[state] ?? 0), 0);
}

/** What accepting this document would classify it as at a chosen site. Asked only when a site is
 * chosen: with none there is nothing to classify at (the jurisdiction decides the factor set). */
export function useClassificationPreview(
  documentId: string,
  facilityId: string,
  fuelUse: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: processingKeys.classification(documentId, facilityId, fuelUse),
    retry: false,
    queryFn:
      enabled && facilityId
        ? async (): Promise<ClassificationPreview> => {
            const { data, error, response } = await api.GET(
              "/api/v1/evidence/{document_id}/classification-preview",
              {
                params: {
                  path: { document_id: documentId },
                  query: { facility_id: facilityId, ...(fuelUse ? { fuel_use: fuelUse } : {}) },
                },
              },
            );
            if (error || !data) {
              throw refusalFrom(response, error, "The scope and category could not be previewed.");
            }
            return data;
          }
        : skipToken,
  });
}

/** The vendor the register holds that equals what the document says, after trimming case and
 * spacing - or nothing. **A suggestion**: it is `current` only after a person confirmed it. */
export function useVendorSuggestion(documentId: string, enabled = true) {
  return useQuery({
    queryKey: processingKeys.vendor(documentId),
    enabled,
    retry: false,
    queryFn: async (): Promise<VendorSuggestion> => {
      const { data, error, response } = await api.GET("/api/v1/evidence/{document_id}/vendor-suggestion", {
        params: { path: { document_id: documentId } },
      });
      if (error || !data) throw refusalFrom(response, error, "The vendor could not be looked up.");
      return data;
    },
  });
}

/** A person says which vendor this document belongs to (`null` clears it). Refetches the suggestion
 * and the documents it appears in; the review itself is invalidated too because the document's
 * `version` moved and the next Verify must send the one on screen. */
export function useSetDocumentVendor(documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { vendorId: string | null; expectedVersion?: number }): Promise<DocumentRow> => {
      const { data, error, response } = await api.PUT("/api/v1/evidence/{document_id}/vendor", {
        params: { path: { document_id: documentId } },
        body: {
          vendor_id: input.vendorId,
          ...(input.expectedVersion !== undefined ? { expected_version: input.expectedVersion } : {}),
        },
      });
      if (error || !data) throw refusalFrom(response, error, "That vendor could not be saved.");
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: processingKeys.vendor(documentId) }),
        queryClient.invalidateQueries({ queryKey: evidenceKeys.review(documentId) }),
        queryClient.invalidateQueries({ queryKey: ["evidence", "document", documentId] }),
        queryClient.invalidateQueries({ queryKey: ["evidence", "documents"] }),
      ]);
    },
  });
}
